import React, { useEffect, useState } from "react";

import { Button, DatePicker, IconWrapper, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as CloseIcon } from "@king-ict/eupisi-ui/components/Icon/CloseBig.svg";
import {
  useValidation,
  useDisableBodyScroll,
  enableBodyScroll,
} from "@king-ict/eupisi-ui/hooks";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { DocumentUploadRequest, uploadDocument } from "../../api/nisooApi";
import { ConfirmModal, ConfirmModalState } from "../../components/FormControls/ConfirmModal";
import { useInput } from "../../hooks";
import { DocumentType } from "../../models/Document";
import { ExecutionResolutionDocument, createResolutionDocument, ResolutionDocumentValidationScheme } from "../../models/ProgramRequest";
import { ResolutionDocumentType } from "../../models/ProgramRequest/ResolutionDocumentType";
import styles from "../ModalResolution/ModalResolution.module.scss";
import UploadButton from "../UploadButton";

export interface ConsentModalProps {
  children?: any;
  id?: string;
  open: boolean;
  consent?: ExecutionResolutionDocument;
  minWidth?: boolean;
  hideOverlay?: boolean;
  disableBody?: boolean;
  variant?: "normal" | "medium" | "big";
  showCloseMark?: boolean;
  /** Called when save is clicked and validation is successful */
  onConfirm?: (result: ExecutionResolutionDocument) => void;
  /** Called when Cancel is clicked. */
  onClose: (event: React.MouseEvent<any, MouseEvent>) => void;
  onFinish: () => void;
  documentToolTip: string,
  documentFilter: string,
  programRequestId?: number,
}

export const ConsentModal = ({
  children,
  id,
  open = false,
  consent,
  minWidth = true,
  hideOverlay = false,
  disableBody = true,
  variant = "normal",
  showCloseMark = false,
  onConfirm,
  onClose,
  onFinish,
  documentToolTip,
  documentFilter,
  programRequestId
}: ConsentModalProps) => {
  useDisableBodyScroll(disableBody, open);
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const defaultResolutionType = ResolutionDocumentType.OtherLegalEntityLocationDecision;

  const [consentInternal, setConsentInternal] = useState<ExecutionResolutionDocument>(consent ?? createResolutionDocument(defaultResolutionType, ""));
  const [confirmModalApproveState, setConfirmModalApproveState] = useState<ConfirmModalState>(ConfirmModalState.Closed);
  const [confirmModalRejectState, setConfirmModalRejectState] = useState<ConfirmModalState>(ConfirmModalState.Closed);

  useEffect(() => {
    //unmount
    return () => {
      enableBodyScroll();
    };
  }, []);

  useEffect(() => {
    if (consent) {
      setConsentInternal(consent);
      // is there a way to reset the validation object?
    }
  }, [consent]);

  const clearModal = (e: React.MouseEvent<any, MouseEvent>) => {
    setConsentInternal(createResolutionDocument(defaultResolutionType, ""));
    onClose(e);
  }

  const [errors, doValidations] = useValidation({
    data: consentInternal,
    validationSchema: ResolutionDocumentValidationScheme, //ConsentValidationScheme,
  });

  // const handleConfirm = async (approved: boolean) => {
  //   const isValid = doValidations();
  //   if (!isValid) return;

  //   await uploadResolutionDocument();

  //   var approvedInternal: boolean | undefined = approved;
  //   if (consentInternal.documentTypeId === ResolutionDocumentType.ArchiveASOO
  //     || consentInternal.documentTypeId === ResolutionDocumentType.ArchiveMZO) {
  //       approvedInternal = undefined;
  //   }

  //   const newConsent: ExecutionResolutionDocument = {
  //     ...consentInternal,
  //     isApproved: approvedInternal,
  //   };
  //   setConsentInternal(newConsent);

  //   if (onConfirm) {
  //     onConfirm(newConsent);
  //   }
  // }

  const handleConfirm = (approved: boolean) => {
    const isValid = doValidations();
    if (!isValid) return;

    if (approved) {
      setConfirmModalApproveState(ConfirmModalState.Confirm);
    }
    else {
      setConfirmModalRejectState(ConfirmModalState.Confirm);
    }
  };

  const doApprove = async () => {
    setConfirmModalApproveState(ConfirmModalState.Closed);
    await uploadResolutionDocument();

    var approvedInternal: boolean | undefined = true;

    if (consentInternal.documentTypeId === ResolutionDocumentType.ArchiveASOO
      || consentInternal.documentTypeId === ResolutionDocumentType.ArchiveMZO) {
      approvedInternal = undefined;
    }

    const newConsent: ExecutionResolutionDocument = {
      ...consentInternal,
      isApproved: approvedInternal,
    };
    setConsentInternal(newConsent);

    if (onConfirm) {
      onConfirm(newConsent);
      setConfirmModalApproveState(ConfirmModalState.Message);
    }
  };

  const doReject = async () => {
    setConfirmModalRejectState(ConfirmModalState.Closed);
    await uploadResolutionDocument();

    var approvedInternal: boolean | undefined = false;

    if (consentInternal.documentTypeId === ResolutionDocumentType.ArchiveASOO
      || consentInternal.documentTypeId === ResolutionDocumentType.ArchiveMZO) {
      approvedInternal = undefined;
    }

    const newConsent: ExecutionResolutionDocument = {
      ...consentInternal,
      isApproved: approvedInternal,
    };
    setConsentInternal(newConsent);

    if (onConfirm) {
      onConfirm(newConsent);
      setConfirmModalRejectState(ConfirmModalState.Message);
    }
  };

  const uploadResolutionDocument = async () => {
    if (consentInternal.resolutionDocument && consentInternal.resolutionDocument.id === -1) {
      var resolutionDocumentRequest: DocumentUploadRequest = {
        document: consentInternal.resolutionDocument!.file!,
        documentTypeId: DocumentType.ProgramExecutionResolution        
      };

      await uploadDocument(resolutionDocumentRequest).then((idDoc) => {
        var resolutionDocument = consentInternal.resolutionDocument;
        resolutionDocument!.id = idDoc;
        setConsentInternal((prev) => ({
          ...prev,
          resolutionDocument: resolutionDocument
        }));
      });
    }
  };

  const getTitle = (): string => {
    switch (consentInternal.documentTypeId) {
      case ResolutionDocumentType.OtherLegalEntityLocationDecision:
        return t("otherLegalEntityLocation.ministryConsentButton");
      case ResolutionDocumentType.ForeignLanguageDecision:
        return t("teacherLanguage.ministryConsentButton");
      case ResolutionDocumentType.HarmonizationDecisionASOO:
        // case ResolutionDocumentType.HarmonizationDecisionMZO:
        return t("harmonization.ministryConsentButton");
      case ResolutionDocumentType.ArchiveASOO:
        return t("revocation.agencyRevocationButton");
      case ResolutionDocumentType.ArchiveMZO:
        return t("revocation.ministryRevocationButton");
      case ResolutionDocumentType.OtherLocationDecision:
        return (t("otherLocations.ministryConsentButton"));
      default:
        return "";
    }
  };

  const getConfirmDescription = (isApprove: boolean): string => {
    switch (consentInternal.documentTypeId) {
      case ResolutionDocumentType.OtherLegalEntityLocationDecision:
        return isApprove ? t("otherLegalEntityLocation.confirmDescription") : t("otherLegalEntityLocation.rejectDescription");
      case ResolutionDocumentType.ForeignLanguageDecision:
        return isApprove ? t("teacherLanguage.confirmDescription") : t("teacherLanguage.rejectDescription");
      // case ResolutionDocumentType.HarmonizationDecisionMZO:
      //   return isApprove ? t("harmonization.confirmDescriptionMZO") : t("harmonization.rejectDescriptionMZO");
      case ResolutionDocumentType.HarmonizationDecisionASOO:
        return isApprove ? t("harmonization.confirmDescriptionASOO") : t("harmonization.rejectDescriptionASOO");
      case ResolutionDocumentType.ArchiveASOO:
        return t("revocation.agencyRevocationDescriptionMessage", { id: programRequestId });
      case ResolutionDocumentType.ArchiveMZO:
        return t("revocation.ministryRevocationDescriptionMessage", { id: programRequestId });
      case ResolutionDocumentType.OtherLocationDecision:
        return isApprove ? t("otherLocations.confirmDescription") : t("otherLocations.rejectDescription");
      default:
        return "";
    }
  };

  const getConfirmMessage = (isApprove: boolean): string => {
    switch (consentInternal.documentTypeId) {
      case ResolutionDocumentType.OtherLegalEntityLocationDecision:
        return isApprove ? t("otherLegalEntityLocation.confirmMessage") : t("otherLegalEntityLocation.rejectMessage");
      case ResolutionDocumentType.ForeignLanguageDecision:
        return isApprove ? t("teacherLanguage.confirmMessage") : t("teacherLanguage.rejectMessage");
      // case ResolutionDocumentType.HarmonizationDecisionMZO:
      case ResolutionDocumentType.HarmonizationDecisionASOO:
        return isApprove ? t("harmonization.confirmMessage") : t("harmonization.rejectMessage");
      case ResolutionDocumentType.ArchiveASOO:
        return t("revocation.agencyRevocationConfirmMessage");
      case ResolutionDocumentType.ArchiveMZO:
        return t("revocation.ministryRevocationConfirmMessage");
      case ResolutionDocumentType.OtherLocationDecision:
        return isApprove ? t("otherLocations.confirmMessage") : t("otherLocations.rejectMessage");
      default:
        return "";
    }
  };

  const getSuccessMessage = (isApprove: boolean): string => {
    switch (consentInternal.documentTypeId) {
      case ResolutionDocumentType.OtherLegalEntityLocationDecision:
        return isApprove ? t("otherLegalEntityLocation.confirmSuccessMessage", { id: programRequestId }) : t("otherLegalEntityLocation.rejectSuccessMessage", { id: programRequestId });
      case ResolutionDocumentType.ForeignLanguageDecision:
        return isApprove ? t("teacherLanguage.confirmSuccessMessage", { id: programRequestId }) : t("teacherLanguage.rejectSuccessMessage", { id: programRequestId });
      // case ResolutionDocumentType.HarmonizationDecisionMZO:
      case ResolutionDocumentType.HarmonizationDecisionASOO:
        return isApprove ? t("harmonization.confirmSuccessMessage", { id: programRequestId }) : t("harmonization.rejectSuccessMessage", { id: programRequestId });
      case ResolutionDocumentType.ArchiveASOO:
      case ResolutionDocumentType.ArchiveMZO:
        return t("revocation.successMessage", { id: programRequestId });
      case ResolutionDocumentType.OtherLocationDecision:
        return isApprove ? t("otherLocations.confirmSuccessMessage", { id: programRequestId }) : t("otherLocations.rejectSuccessMessage", { id: programRequestId });
      default:
        return "";
    }
  };

  function getVariant() {
    if (variant) {
      return styles[variant];
    }
  }

  if (!open || !consent) return null;

  /** Immediately uploads the document to the backend and sets the Id to the object. */
  const handleDocumentAdded = async (event: React.ChangeEvent<HTMLInputElement>, file: File) => {
    var document: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };

    setConsentInternal((prev) => ({
      ...prev,
      resolutionDocument: document
    }));
  };

  /** Removes the document from the object. It does not delete the document on the backend. */
  const handleDocumentRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let document = consentInternal.resolutionDocument;
    if (document === undefined || document.id === undefined) {
      return;
    }

    setConsentInternal((prev) => ({
      ...prev,
      resolutionDocument: undefined,
    }));
  };

  const getDocumentLabelText = () => {
    switch (consentInternal.documentTypeId) {
      case ResolutionDocumentType.OtherLegalEntityLocationDecision:
        return t("request.resolutionDocuments");
      case ResolutionDocumentType.ForeignLanguageDecision:
        return t("request.resolutionDocuments");
      case ResolutionDocumentType.HarmonizationDecisionASOO:
        // case ResolutionDocumentType.HarmonizationDecisionMZO:
        return t("request.resolutionDocuments");
      default:
        return t("request.resolutionDocuments");
    }
  }

  const getConfirmButtonText = (isApprove: boolean) => {
    switch (consentInternal.documentTypeId) {
      case ResolutionDocumentType.ArchiveASOO:
      case ResolutionDocumentType.ArchiveMZO:
        return t("revocation.confirmButton");
      default:
        return isApprove ? t("request.judgeRequestModalConfirm") : t("request.judgeRequestModalReject");
    }
  };

  return (
    <div
      className={`${styles.parentContainer}`}
    >
      <div
        className={`${styles.overlay} ${hideOverlay ? styles.off : styles.on}`}
        onClick={onClose}
      ></div>
      <div
        id={id}
        className={`${styles.container} ${getVariant()} ${minWidth ? styles.minWidth : ""}`}
      >
        <div className={styles.contentWrapper}>
          {getTitle() || showCloseMark ? (
            <div className={styles.header}>
              <div className={styles.title}>
                <Typography variant="largeBold">{getTitle()}</Typography>
              </div>
              <div className={styles.closeMark} onClick={onClose}>
                {showCloseMark ? (
                  <IconWrapper color="black-color">
                    <CloseIcon aria-label={t("accessibility.closeIcon")} />
                  </IconWrapper>
                ) : (
                  <></>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
          <div className={styles.content}>{children}</div>
          <Row className="">
            <Col md={12}>
              <div
              >
                <Row>
                  <Col md={12}>
                    <UploadButton
                      label={getDocumentLabelText()}
                      className={styles.uploadButtonInRowSection}
                      value={consentInternal.resolutionDocument}
                      name={nameof<typeof consentInternal>("resolutionDocument")}
                      onFileAdded={(file, e) => handleDocumentAdded(e, file)}
                      onFileRemoved={(e) => handleDocumentRemoved(e)}
                      toolTip={documentToolTip}
                      fileFilter={documentFilter}
                      errorText={errors?.resolutionDocument}
                      documentType={DocumentType.ProgramExecutionResolution}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <TextField
                      label={t("common.classificationMark")}
                      value={consentInternal.classificationMark}
                      name={nameof<typeof consentInternal>("classificationMark")}
                      maxLength={128}
                      onChange={handleChange(setConsentInternal)}
                      errorText={errors?.classificationMark}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <TextField
                      label={t("request.resolutionRegisterNumber")}
                      value={consentInternal.registryNumber}
                      name={nameof<typeof consentInternal>("registryNumber")}
                      maxLength={128}
                      onChange={handleChange(setConsentInternal)}
                      errorText={errors?.registryNumber}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <DatePicker
                      label={t("request.committeeMembershipIssueDate")}
                      value={consentInternal.issueDate}
                      name={nameof<typeof consentInternal>("issueDate")}
                      onChange={handleChange(setConsentInternal)}
                      errorText={errors?.issueDate}
                      minYear={new Date().getFullYear() - 25}
                      maxYear={new Date().getFullYear() + 25}
                    />
                  </Col>
                </Row>
                {(false) &&
                  <>
                    <Row>
                      <Col md={12}>
                        <DatePicker
                          label={t("common.dateFrom")}
                          value={consentInternal.dateFrom}
                          name={nameof<typeof consentInternal>("dateFrom")}
                          onChange={handleChange(setConsentInternal)}
                          errorText={errors?.dateFrom}
                          minYear={new Date().getFullYear() - 25}
                          maxYear={new Date().getFullYear() + 25}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <DatePicker
                          label={t("common.dateTo")}
                          value={consentInternal.dateTo}
                          name={nameof<typeof consentInternal>("dateTo")}
                          onChange={handleChange(setConsentInternal)}
                          errorText={errors?.dateTo}
                          minYear={new Date().getFullYear() - 25}
                          maxYear={new Date().getFullYear() + 25}
                        />
                      </Col>
                    </Row>
                  </>
                }
              </div>
            </Col>
          </Row>
        </div>
        <div className={styles.confirmButtonsContainer}>
          <div>
            <Button color="primary" outline onClick={(e) => { clearModal(e) }}>
              {t("common.cancel")}
            </Button>
          </div>
          <div>
            {(consentInternal.documentTypeId !== ResolutionDocumentType.ArchiveASOO
              && consentInternal.documentTypeId !== ResolutionDocumentType.ArchiveMZO) &&
              <Button className={styles.rejectButtons} onClick={() => handleConfirm(false)}>
                {t("request.judgeRequestModalReject")}
              </Button>
            }
            <Button color="primary" onClick={() => handleConfirm(true)}>
              {getConfirmButtonText(true)}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmModal
        confirmState={confirmModalApproveState}
        title={getTitle()}
        confirmDescription={getConfirmDescription(true)}
        confirmMessage={getConfirmMessage(true)}
        successMessage={getSuccessMessage(true)}
        onClickConfirm={doApprove}
        onClickClose={() => { setConfirmModalApproveState(ConfirmModalState.Closed); onFinish(); }}
        onClickCancel={() => setConfirmModalApproveState(ConfirmModalState.Closed)}
        confirmButton={getConfirmButtonText(true)}
        cancelButton={t("common.cancel")}
      />
      <ConfirmModal
        confirmState={confirmModalRejectState}
        title={getTitle()}
        confirmDescription={getConfirmDescription(false)}
        confirmMessage={getConfirmMessage(false)}
        successMessage={getSuccessMessage(false)}
        onClickConfirm={doReject}
        onClickClose={() => { setConfirmModalRejectState(ConfirmModalState.Closed); onFinish(); }}
        onClickCancel={() => setConfirmModalRejectState(ConfirmModalState.Closed)}
        confirmButton={getConfirmButtonText(false)}
        cancelButton={t("common.cancel")}
      />
    </div>
  );
};
